"use client"

import React from "react"
import Image from "next/image"

import { formatDate, formatPrice } from "@/lib/helper"
import { AccountContext } from "@/lib/storefront/account/account-service"

interface AccountQuoteClientProps {
  data: any
  isQuote: boolean
  accountContext: AccountContext
}

export default function AccountQuoteClient({ data, isQuote, accountContext }: AccountQuoteClientProps) {
  if (!data) return <div>Kein Angebot/Anfrage gefunden.</div>

  return (
    <div className="bg-white rounded-lg shadow p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex flex-col items-center mb-6">
        <div className="flex gap-2 mb-2">
          {/* Render up to 3 images */}
          {data.items
            ?.slice(0, 3)
            .map((item: any, idx: number) => (
              <Image
                key={idx}
                src={item.image || "/img/placeholder.png"}
                alt=""
                width={80}
                height={80}
                className="rounded"
              />
            ))}
        </div>
        <div className="text-2xl font-bold">Vorgangsnummer: {data.number}</div>
        <div className="flex gap-4 mt-2">
          <input className="border rounded px-2 py-1" placeholder="Fügen Sie Ihre Referenz ein." />
          <input
            className="border rounded px-2 py-1"
            placeholder="Fügen Sie ihrer Angebotsanfrage einen Kommentar hinzu (optional)"
          />
        </div>
      </div>

      {/* Items */}
      <div className="bg-gray-50 rounded p-4 mb-6">
        {data.items?.map((item: any, idx: number) => (
          <div key={idx} className="flex border-b last:border-b-0 py-4">
            <div className="w-32 flex-shrink-0 flex items-center">
              <Image src={item.image || "/img/placeholder.png"} alt="" width={80} height={80} className="rounded" />
            </div>
            <div className="flex-1 px-4">
              <div className="font-bold">{item.displayName}</div>
              <div className="text-xs text-gray-600">Art.-Nr.: {item.productSKU}</div>
              {/* Add more item details as needed */}
              <div className="text-xs mt-2">{item.description}</div>
            </div>
            <div className="flex flex-col items-end justify-between w-40">
              <div className="text-right text-sm text-gray-500">
                Stückpreis: {formatPrice(accountContext.isoLocale, item.price?.value, item.price?.currency)}
              </div>
              <div className="font-bold text-lg">
                {formatPrice(accountContext.isoLocale, item.total?.value, item.total?.currency)}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Summary */}
      <div className="flex flex-col items-end gap-1 mb-6">
        <div className="flex gap-8">
          <div>Gesamtsumme (netto):</div>
          <div className="font-bold">
            {formatPrice(
              accountContext.isoLocale,
              data.totalNet?.value || data.totalNetAmount?.value,
              data.totalNet?.currency || data.totalNetAmount?.currency
            )}
          </div>
        </div>
        <div className="flex gap-8">
          <div>Mehrwertsteuer:</div>
          <div className="font-bold">
            {formatPrice(
              accountContext.isoLocale,
              data.tax?.value || data.taxAmount?.value,
              data.tax?.currency || data.taxAmount?.currency
            )}
          </div>
        </div>
        <div className="flex gap-8 text-lg">
          <div>Gesamtsumme (brutto):</div>
          <div className="font-bold">
            {formatPrice(accountContext.isoLocale, data.total?.value, data.total?.currency)}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4 justify-end">
        {!isQuote && <button className="bg-red-700 text-white px-8 py-2 rounded font-bold">ANGEBOT ERSTELLEN</button>}
        <button className="border-2 border-red-700 text-red-700 px-8 py-2 rounded font-bold bg-white">
          ANFRAGE SPEICHERN
        </button>
      </div>
    </div>
  )
}
